import { Chip, Theme, makeStyles, Tooltip } from '@material-ui/core';
import {
  ChipLabels,
  PortfolioFilter,
  PortfolioFilterOptions,
  FilterViewType,
} from '../../../state/contexts/portfolioPage';
import {
  serialNumberOperatorOptions,
  equipmentFirmwareVersionOperatorOptions,
  onlineStatusOptions,
  signalStrengthOptions,
  equipmentQuantityOperatorOptions,
} from './filterOptionsMock';

interface FilterChipsProps {
  currentFilters: PortfolioFilter;
  filterOptions: PortfolioFilterOptions;
  onChipRemove: (keys: (keyof PortfolioFilter)[], filterView?: FilterViewType) => void;
  onChipClick: (view?: FilterViewType) => void;
}

const FilterChips = ({
  currentFilters,
  filterOptions,
  onChipRemove,
  onChipClick,
}: FilterChipsProps) => {
  const classes = useStyles();

  const HIDDEN_FILTER_KEYS: Array<keyof PortfolioFilter> = [
    'serialNumberOperator',
    'equipmentFirmwareVersionOperator',
    'equipmentQuantityOperator',
    'equipmentQuantity',
  ];

  if (currentFilters.equipmentFirmwareVersion) {
    HIDDEN_FILTER_KEYS.push('equipmentType');
  }

  const getListOfActiveFilterLabels = (key: keyof PortfolioFilter) => {
    const currentValues = currentFilters[key];
    const availableValues = filterOptions[key as keyof PortfolioFilterOptions] || [];

    if (!currentValues) {
      return [];
    }

    const filterHandlers = {
      endUserName: () => [currentValues.toString()],
      endUserAddress: () => [currentValues.toString()],
      installerCompanyName: () => {
        const installerCompanyNames = filterOptions.installers
          .filter((option) => currentFilters.installerCompanyName?.includes(option.value))
          .map((option) => option.name);

        return installerCompanyNames;
      },
      equipmentType: () => {
        const equipmentQuantityOperator = equipmentQuantityOperatorOptions.find(
          (option) => option.value === currentFilters.equipmentQuantityOperator
        );
        const equipmentTypes = filterOptions.equipmentTypes
          ?.filter((option) => currentFilters.equipmentType?.includes(option.value))
          .map((option) => option.name);
        return equipmentTypes.map(
          (equipmentType) =>
            `${equipmentType} ${equipmentQuantityOperator?.name ?? ''} ${
              currentFilters.equipmentQuantity ?? ''
            }`
        );
      },
      serialNumber: () => {
        const serialNumberOperator = serialNumberOperatorOptions.find(
          (option) => option.value === currentFilters.serialNumberOperator
        );
        return [`${serialNumberOperator?.name ?? ''} ${currentValues.toString()}`];
      },
      equipmentFirmwareVersion: () => {
        const equipmentFirmwareVersionOperator = equipmentFirmwareVersionOperatorOptions.find(
          (option) => option.value === currentFilters.equipmentFirmwareVersionOperator
        );

        const equipmentType = filterOptions.equipmentTypes
          ?.filter((option) => currentFilters.equipmentType?.includes(option.value))
          .map((option) => option.name);
        return [
          `${equipmentType} ${equipmentFirmwareVersionOperator?.name ?? ''} ${
            currentFilters.equipmentFirmwareVersion ?? ''
          }`,
        ];
      },
      onlineStatus: () => {
        const onlineStatus = onlineStatusOptions.find(
          (option) => option.value === currentFilters.onlineStatus
        );
        return [`${onlineStatus?.name}`];
      },
      signalStrength: () => {
        const signalStrength = signalStrengthOptions.find(
          (option) => option.value === currentFilters.signalStrength
        );
        return [`${signalStrength?.name}`];
      },
    };

    if (key in filterHandlers) {
      return filterHandlers[key as keyof typeof filterHandlers]() || [];
    }

    if (Array.isArray(currentValues)) {
      if (Array.isArray(availableValues) && availableValues.length > 0) {
        return availableValues;
      }
      return currentValues;
    }

    return [currentValues.toString()];
  };

  const chipLabel = (key: keyof PortfolioFilter) => {
    return `${ChipLabels[key]}: ${getListOfActiveFilterLabels(key).join(', ')}`;
  };

  const handleRemoveChip = (key: keyof PortfolioFilter) => {
    onChipRemove([key], key as FilterViewType);
  };

  return (
    <>
      {(Object.keys(currentFilters) as (keyof PortfolioFilter)[]).map((key) => {
        if (HIDDEN_FILTER_KEYS.includes(key)) {
          return null;
        }
        if (!currentFilters[key]) {
          return null;
        }
        const label = chipLabel(key);

        return (
          <Tooltip key={key} title={label}>
            <Chip
              classes={{
                root: classes.chip,
              }}
              size="medium"
              label={label}
              onDelete={() => handleRemoveChip(key)}
              onClick={() => {
                if (key) {
                  onChipClick(key as FilterViewType);
                }
              }}
            />
          </Tooltip>
        );
      })}
    </>
  );
};

const useStyles = makeStyles<Theme>((theme) => ({
  chip: {
    margin: theme.spacing(0.1, 0.2),
    maxWidth: '320px',
  },
}));

export default FilterChips;
